/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  Warning,
  TextArea,
  InfoDisplay
} from '../'

class EmployerNotes extends React.Component {
  static propTypes = {
    employerId: PropTypes.string.isRequired,
    title: PropTypes.string,
    className: PropTypes.string
  }

  static defaultProps = {
    title: 'Notes',
    className: ''
  }

  state = {
    employerNotes: '',
    editEmployerNotes: false,
    saveInProgress: false
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.employerNotes === '' && !prevProps.data.employer && this.props.data.employer) {
      this.setState({ employerNotes: this.props.data.employer.notes })
    }
  }

  goReadOnly = () => this.setState({ editEmployerNotes: false, saveInProgress: false })

  noChangesToSave = () => this.props.data.employer.notes === this.state.employerNotes

  goEditNote = () => this.setState({ editEmployerNotes: true })

  handleNotesUpdate = () => {
    this.setState({ saveInProgress: true })

    let input = {
      id: this.props.data.employer.id,
      notes: this.state.employerNotes
    }

    if (!this.noChangesToSave()) {
      this.props.editEmployerNotes({ variables: { input } })
        .then(this.goReadOnly)
        .catch(() => this.setState({ saveInProgress: false }))
    } else {
      this.setState({ saveInProgress: false })
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' size='small' />
    }

    if (this.props.data.error) {
      return <Warning>Failed to load employer notes.</Warning>
    }

    const employer = this.props.data.employer

    return (
      <InfoDisplay
        title={this.props.title}
        rows={this.state.editEmployerNotes
          ? <TextArea
              value={this.state.employerNotes}
              onChange={e => this.setState({ employerNotes: e.target.value })}
              rows={5}
            />
          : <tbody>
              <tr>
                <td className='whitespace-pre_wrap'>
                  {employer.notes
                    ? employer.notes.trim()
                    : 'none'
                  }
                </td>
              </tr>
            </tbody>
        }
        edit={this.state.editEmployerNotes}
        onCancelClick={this.goReadOnly}
        onSaveClick={this.handleNotesUpdate}
        onEditClick={this.goEditNote}
        titleBold={false}
        saveDisabled={this.noChangesToSave() || this.state.saveInProgress}
        saveButtonLabel={this.state.saveInProgress ? 'Saving...' : null}
        className={`slds-m-bottom_x-large ${this.props.className}`}
      />
    )
  }
}

const getEmployerNotes = gql`
  query($id: ID!) {
    employer(id: $id) {
      id
      notes
    }
  }
`

const editEmployerNotes = gql`
  mutation editEmployerNotes($input: EmployerMutationInput!) {
    employerMutate(input: $input) {
      employer {
        id
        notes
      }
    }
  }
`

const withData =
  graphql(getEmployerNotes, {
    options: ({ employerId }) => ({
      variables: {
        id: employerId
      }
    })
  })

const withContactMutate =
  graphql(editEmployerNotes, {
    name: 'editEmployerNotes'
  })

export default compose(
  withData,
  withContactMutate
)(EmployerNotes)



// WEBPACK FOOTER //
// ./src/components/employer-notes/index.js